import React from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { C } from "../../../common/theme";
import { BarList, Panel } from "../../../ui";
import { formatCount } from "../platformData";

const COLLAPSED_ROWS = 6;

const share = (views: number, total: number) => {
  const pct = total > 0 ? (views / total) * 100 : 0;
  return pct > 0 && pct < 1 ? "<1%" : `${Math.round(pct)}%`;
};

export interface CountRow {
  key: string;
  label: string;
  views: number;
  sessions: number;
}

interface CountListProps {
  title: string;
  rows: CountRow[];
  /** All views in the period, for the share; the rows are only the top N */
  total: number;
  help?: string;
  empty?: string;
}

/** Top-N by page views as a ranked bar list. */
export default function CountList({ title, rows, total, help, empty = "No page views in this period" }: CountListProps) {
  const [expanded, setExpanded] = React.useState(false);
  const shown = expanded ? rows : rows.slice(0, COLLAPSED_ROWS);

  return (
    <Panel padding={1.5} sx={{ height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {help && (
          <Tooltip title={help}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: C.faint }} />
          </Tooltip>
        )}
        <Typography variant="overline" sx={{ ml: "auto", color: C.muted }}>
          views
        </Typography>
      </Box>
      <BarList
        empty={empty}
        max={rows.reduce((acc, r) => Math.max(acc, r.views), 0) || 1}
        items={shown.map((r) => ({
          key: r.key,
          label: <span title={r.label}>{r.label}</span>,
          value: r.views,
          display: formatCount(r.views),
          sub: `${formatCount(r.sessions)} session${r.sessions === 1 ? "" : "s"} · ${share(r.views, total)}`,
        }))}
      />
      {rows.length > COLLAPSED_ROWS && (
        <Button size="small" onClick={() => setExpanded((e) => !e)} sx={{ alignSelf: "flex-start", minHeight: 36 }}>
          {expanded ? "Show fewer" : `Show all ${rows.length}`}
        </Button>
      )}
    </Panel>
  );
}
